import { useState } from "react";

import CloseIcon from "@mui/icons-material/CloseRounded";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
} from "@mui/material";
import { format } from "date-fns";

import { EdictEntryResponse, EdictSource, EdictTransferResponse, TransferEdictRequest } from "@/api";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EdictTransferResultDialog } from "@/common/components/edict-transfer/EdictTransferResultDialog";
import { useTransferEdict } from "@/common/components/edict-transfer/hooks/useTransferEdict";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { EdictDetailField } from "./EdictDetailField";
import { useEdictDocumentQuery } from "./hooks/useEdictDocumentQuery";

const ContentRows = 14;

interface Props {
  entry: EdictEntryResponse;
  source: EdictSource;
  onClose: () => void;
}

export function EdictDetailsDialog({ entry, source, onClose }: Props) {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const [transferResult, setTransferResult] = useState<EdictTransferResponse | null>(null);

  const needsDocument = entry.content === null && entry.sourceReference !== null;
  const { data, isFetching, isError } = useEdictDocumentQuery(needsDocument ? entry.sourceReference : null);
  const { mutate: transferEdict, isPending: isTransferring } = useTransferEdict(setTransferResult);

  const address = entry.address ?? data?.address ?? "";
  const court = entry.court ?? data?.court ?? "";
  const fileNumber = data?.fileNumber ?? entry.fileNumber ?? "";
  const content = entry.content ?? data?.content ?? "";

  // Only an entry that is matched to a client file has somewhere to transfer to, and only the
  // insolvency-edict register issues the handle the transfer reads the edict back through. Everything the
  // transfer writes — including which register the edict belongs to — is read off that edict by the
  // backend, which is the side that knows what the transfer would write: the button stays offered and
  // answers.
  const transferRequest: TransferEdictRequest | null =
    entry.clientId !== null && entry.sourceReference !== null
      ? { clientId: entry.clientId, sourceReference: entry.sourceReference }
      : null;

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{ p: 5, pb: 3, display: "flex", alignItems: "center", justifyContent: "space-between" }}
          variant="h1"
        >
          {t(source === EdictSource.Edikt ? "edicts.detailDialog.titleEdikt" : "edicts.detailDialog.titleOzuf")}
          <IconButton onClick={onClose} aria-label={t("common.close")}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 5, pt: 0 }}>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t("edicts.detailDialog.error")}
            </Alert>
          )}
          <Grid container rowSpacing={2} columnSpacing={2} sx={{ pt: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              {/* Only a matched entry has a client file to open, so an unmatched one shows the name as plain text. */}
              <EdictDetailField
                label={t("edicts.detailDialog.name")}
                value={entry.debtorName}
                link={
                  entry.clientId !== null ? (
                    <ClientLink clientId={entry.clientId} name={entry.debtorName} variant="body2" openInNewTab />
                  ) : undefined
                }
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EdictDetailField
                label={t("edicts.detailDialog.birthday")}
                value={entry.birthday !== null ? format(entry.birthday, "P", { locale: locales[language] }) : ""}
              />
            </Grid>
            <Grid size={12}>
              <EdictDetailField label={t("edicts.detailDialog.address")} value={address} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EdictDetailField label={t("edicts.detailDialog.fileNumber")} value={fileNumber} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EdictDetailField label={t("edicts.detailDialog.court")} value={court} />
            </Grid>
            <Grid size={12}>
              {/* The spinner sits over the field rather than in the flow, so the overlay does not resize once
                the register answers. */}
              <Box sx={{ position: "relative" }}>
                <EdictDetailField label={t("edicts.detailDialog.content")} value={content} rows={ContentRows} />
                {isFetching && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <CircularProgress aria-label={t("edicts.detailDialog.loading")} />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 5, pt: 0, justifyContent: "space-between" }}>
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
          {transferRequest !== null && (
            <Tooltip title={t("edicts.detailDialog.transferTooltip")}>
              <Button
                variant="contained"
                aria-label={t("edicts.detailDialog.transferAriaLabel")}
                loading={isTransferring}
                onClick={() => transferEdict(transferRequest)}
              >
                {t("edicts.detailDialog.transfer")}
              </Button>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>
      {transferResult !== null && (
        <EdictTransferResultDialog result={transferResult} onClose={() => setTransferResult(null)} />
      )}
    </>
  );
}
